import React from 'react';
import { Github, Linkedin, Twitter, ArrowUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';

const Footer: React.FC = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      tagline: "Empowering the future of technology, rooted in human nature.",
      sections: {
        services: {
          title: "Services",
          links: [
            { text: "AI Training", href: "/services/ai-training" },
            { text: "Process Automation", href: "/services/process-automation" },
            { text: "Digital Solutions", href: "/services/digital-solutions" },
            { text: "Chatbot Development", href: "/services/chatbots" },
            { text: "Data Analytics", href: "/services/analytics" },
            { text: "Innovation Consulting", href: "/services/consulting" }
          ]
        },
        connect: {
          title: "Connect",
          social: {
            twitter: "https://twitter.com/ednx",
            linkedin: "https://linkedin.com/company/ednx",
            github: "https://github.com/ednx"
          }
        },
        legal: {
          title: "Legal",
          links: [
            { text: "Impressum", href: "/impressum" },
            { text: "Privacy Policy", href: "/privacy" },
            { text: "Terms", href: "/terms" }
          ]
        }
      },
      copyright: "All rights reserved.",
      cookieSettings: "Cookie Settings",
      withdrawal: "Right of Withdrawal",
      backToTop: "Back to top"
    },
    de: {
      tagline: "Wir gestalten die Zukunft der Technologie, verwurzelt in der menschlichen Natur.",
      sections: {
        services: {
          title: "Dienstleistungen",
          links: [
            { text: "KI-Training", href: "/services/ai-training" },
            { text: "Prozessautomatisierung", href: "/services/process-automation" },
            { text: "Digitale Lösungen", href: "/services/digital-solutions" },
            { text: "Chatbot-Entwicklung", href: "/services/chatbots" },
            { text: "Datenanalyse", href: "/services/analytics" },
            { text: "Innovationsberatung", href: "/services/consulting" }
          ]
        },
        connect: {
          title: "Verbinden",
          social: {
            twitter: "https://twitter.com/ednx",
            linkedin: "https://linkedin.com/company/ednx",
            github: "https://github.com/ednx"
          }
        },
        legal: {
          title: "Rechtliches",
          links: [
            { text: "Impressum", href: "/impressum" },
            { text: "Datenschutz", href: "/datenschutz" },
            { text: "AGB", href: "/agb" }
          ]
        }
      },
      copyright: "Alle Rechte vorbehalten.",
      cookieSettings: "Cookie-Einstellungen",
      withdrawal: "Widerrufsrecht",
      backToTop: "Nach oben"
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <footer className="bg-secondary-black/90 border-t border-primary-mint/10 py-16 relative">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          <div>
            <div className="mb-6">
              <Link to="/" className="block">
                <img 
                  src="/EDENXFINALLOGO.svg" 
                  alt="EDNX Logo" 
                  className="h-24 w-24 transform hover:scale-105 transition-transform duration-300" 
                />
              </Link>
            </div>
            <p className="text-secondary-cream/60 text-lg mb-6">
              {content[language].tagline}
            </p>
          </div>
          
          <div>
            <h3 className="text-primary-mint font-semibold mb-6 text-xl">
              {content[language].sections.services.title}
            </h3>
            <ul className="space-y-4 text-secondary-cream/60">
              {content[language].sections.services.links.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.href}
                    className="hover:text-primary-mint transition-colors block"
                  >
                    {link.text}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-primary-mint font-semibold mb-6 text-xl">
              {content[language].sections.connect.title}
            </h3>
            <div className="flex space-x-6 mb-8">
              <a 
                href={content[language].sections.connect.social.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary-cream/60 hover:text-primary-mint transition-colors transform hover:scale-110"
              >
                <Twitter className="w-8 h-8" />
              </a>
              <a 
                href={content[language].sections.connect.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary-cream/60 hover:text-primary-mint transition-colors transform hover:scale-110"
              >
                <Linkedin className="w-8 h-8" />
              </a>
              <a 
                href={content[language].sections.connect.social.github}
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary-cream/60 hover:text-primary-mint transition-colors transform hover:scale-110"
              >
                <Github className="w-8 h-8" />
              </a>
            </div>
            <div className="space-y-4 text-secondary-cream/60">
              <h4 className="font-semibold text-primary-mint">
                {content[language].sections.legal.title}
              </h4>
              <ul className="space-y-2">
                {content[language].sections.legal.links.map((link, index) => (
                  <li key={index}>
                    <Link 
                      to={link.href}
                      className="hover:text-primary-mint transition-colors block"
                    >
                      {link.text}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-primary-mint/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center text-secondary-cream/40 text-sm">
            <p>&copy; {new Date().getFullYear()} EDNX. {content[language].copyright}</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link 
                to="/cookies"
                className="hover:text-primary-mint transition-colors"
              >
                {content[language].cookieSettings}
              </Link>
              <Link 
                to="/widerruf"
                className="hover:text-primary-mint transition-colors"
              >
                {content[language].withdrawal}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Back to top button */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 bg-primary-mint/10 hover:bg-primary-mint/20 
                 border border-primary-mint/30 text-primary-mint rounded-full p-3
                 transition-all duration-300 transform hover:scale-110 group
                 focus:outline-none focus:ring-2 focus:ring-primary-mint/50"
        aria-label={content[language].backToTop}
      >
        <ArrowUp className="w-6 h-6 transform group-hover:-translate-y-1 transition-transform" />
      </button>
    </footer>
  );
};

export default Footer;